import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Alert, Box, BoxProps, StackView, Theme } from 'localmed-core';
import React from 'react';
import { Crosshair } from 'react-feather';
import IconCircle from '../../../legacyCore/icons/IconCircle';
import Navigate from '../../../legacyCore/icons/Navigate';
import Link from '../../../shared/components/Link';
import LoadingMessage from '../../../shared/components/LoadingMessage';
import SearchInputGroup from '../../../shared/components/SearchInputGroup';
import { useLocationForm } from '../OfficeListMapV2/hooks';
import { Coordinates, Office } from '../OfficeListMapV2/types';
import OfficeList from './OfficeList';
import style from './OfficeListMapV1.scss';

const BoxStyle = styled(Box)<BoxProps, Theme>(
  ({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.grays[100]};
  `
);

interface OfficeListContainerProps {
  loading?: boolean;
  coordinates: Coordinates | null;
  setCoordinates: (coordinates: Coordinates | null) => void;
  location: string | null;
  setLocation: (location: string | null) => void;
  activeOffice: Office | null;
  onOfficeHover: (office: Office | null) => void;
  offices: Office[];
}

export default function OfficeListContainer({
  loading,
  coordinates,
  setCoordinates,
  location,
  setLocation,
  offices,
  activeOffice,
  onOfficeHover,
}: OfficeListContainerProps) {
  const locationForm = useLocationForm({ coordinates, setCoordinates, location, setLocation });
  return (
    <div className={style['practice-page__box']}>
      <div className={style['practice-header']} data-qa="practice-widget__header">
        <IconCircle color="red">
          <Navigate />
        </IconCircle>
        <h3>Which office will you be visiting?</h3>
      </div>
      <BoxStyle padding="md">
        {locationForm.status === 'ERROR' && (
          <Alert tier="error" marginBottom="md">
            Could not find your location.
          </Alert>
        )}
        <StackView spacing="sm">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              locationForm.onSubmit();
            }}
          >
            <SearchInputGroup
              id="location"
              name="location"
              placeholder="Search by Zip…"
              value={locationForm.searchValue}
              onFocus={() => {
                locationForm.onInputFocus();
              }}
              onChange={(event) => locationForm.setSearchValue(event.target.value)}
              buttonDisabled={
                locationForm.status === 'LOADING' || locationForm.status === 'CURRENT_LOCATION'
              }
            />
          </form>
          {(locationForm.status === 'IDLE' ||
            locationForm.status === 'ERROR' ||
            locationForm.status === 'CUSTOM') && (
            <Box fontSize="sm">
              <Link onClick={() => locationForm.onGetLocation()}>
                <StackView spacing="xs" direction="row">
                  <Crosshair size="1em" />
                  <Box>Use My Location</Box>
                </StackView>
              </Link>
            </Box>
          )}
        </StackView>
      </BoxStyle>
      {loading ? (
        <BoxStyle padding="md">
          <LoadingMessage />
        </BoxStyle>
      ) : (
        <OfficeList
          activeOffice={activeOffice}
          offices={offices}
          onOfficeHover={onOfficeHover}
          hasSelectedLocation={
            locationForm.status === 'LOADING' ||
            locationForm.status === 'CURRENT_LOCATION' ||
            locationForm.status === 'CUSTOM'
          }
        />
      )}
    </div>
  );
}
